import { useCallback, useMemo } from 'react';
import type { ApolloCache, FetchResult } from '@apollo/client';
import type {
  UpdateClientPreferencesInput,
  UpdateUserPreferenceMutation,
  UserPreferenceKeysFragment,
  UserPreferencesFragment,
} from '@frameio/graphql-client/generated';
import {
  UserPreferences,
  useUpdateUserPreferenceMutation,
} from '@frameio/graphql-client/generated';
import { useMeCachedQuery } from './useMeCachedQuery/useMeCachedQuery';

/**
 * Hook for managing user preferences that are stored on the backend.
 *
 * This hook provides access to the current user's preferences and a method to update them.
 * It handles optimistic updates and cache management through Apollo Client.
 *
 * @returns {Object} An object containing:
 *   - userPrefs: The current user preferences (excluding __typename). These are fetched with the Me query on app load and cached.
 *   - setUserPrefs: A function to update user preferences. This will update the user preferences in the cache and the backend.
 *
 * @example
 * ```tsx
 * const { userPrefs, setUserPrefs } = useBackendUserPrefs();
 *
 * // Update a preference
 * setUserPrefs({ somePreference: 'newValue' });
 * ```
 *
 * @remarks
 * - Uses optimistic updates for immediate UI feedback
 * - Automatically handles cache updates through Apollo Client
 * - Returns empty object if user is not authenticated
 * - Requires authenticated user to make updates
 */
export const useBackendUserPrefs = (): {
  userPrefs: UserPreferenceKeysFragment;
  setUserPrefs: (input: UpdateClientPreferencesInput) => void;
} => {
  const { data } = useMeCachedQuery();

  const clientPreferences =
    data?.me?.__typename === 'AuthenticatedUser'
      ? data?.me?.clientPreferences
      : null;

  const userPrefs = useMemo(
    () => clientPreferences ?? ({} as UserPreferenceKeysFragment),
    [clientPreferences]
  );

  const [_setUserPrefs] = useUpdateUserPreferenceMutation();

  const setUserPrefs = useCallback(
    (input: UpdateClientPreferencesInput) => {
      if (data?.me?.__typename !== 'AuthenticatedUser' || !data?.me?.id) return;

      const userId = data.me.id;

      _setUserPrefs({
        variables: {
          input,
        },
        optimisticResponse: {
          updateClientPreferences: {
            userClientPreferences: {
              ...userPrefs,
              ...input,
              ...(input.locales &&
                typeof input.locales === 'object' && {
                  locales: { ...userPrefs.locales, ...input.locales },
                }),
              __typename: 'UserClientPreferences',
            },
          },
        },
        update: (
          cache: ApolloCache<any>,
          results: FetchResult<UpdateUserPreferenceMutation>
        ) => {
          const userPreferences =
            results.data?.updateClientPreferences?.userClientPreferences;

          cache.updateFragment<UserPreferencesFragment>(
            {
              id: cache.identify({
                __typename: 'AuthenticatedUser',
                id: userId,
              }),
              fragment: UserPreferences,
              fragmentName: 'UserPreferences',
            },
            (previousData) => {
              if (!previousData) return null;
              return {
                ...previousData,
                clientPreferences: userPreferences,
              };
            }
          );
        },
      });
    },
    [_setUserPrefs, data?.me?.__typename, data?.me?.id, userPrefs]
  );

  return {
    userPrefs,
    setUserPrefs,
  };
};
